import { useEffect, useMemo, useState } from "react";
import { getStatistics, getSalesStatistics, getSSTStatistics, getTopUpStatistics, getProfitStatistics, getUserStatistics, getDriverStatistics, getManualTopUpStatistics } from "../../api/dashboard";
import { StatisticsChart } from "../../components/Dashboard";
import { Card, Row, Col } from "react-bootstrap";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend, CartesianGrid } from "recharts";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { PageWrapper } from "../../components";

const Dashboard = ({ }) => {
    const { t } = useTranslation()
    const [data, setData] = useState(null)
    const [cardData, setCardData] = useState({
        sales: null,
        sst: null,
        manualTopup: null,
        topup: null,
        profit: null,
        user: null,
        driver: null
    })

    const [cardLoading, setCardLoading] = useState({
        sales: false,
        sst: false,
        manualTopup: false,
        topup: false,
        profit: false,
        user: false,
        driver: false
    })

    useEffect(() => {
        getData()
    }, [])

    const getData = async () => {
        const response = await getStatistics()
        setData(response)
    }

    const handleTabChange = async (cardType, dateRange, tabKey) => {
        // Set loading state for the specific card
        setCardLoading(prev => ({ ...prev, [cardType]: true }))

        try {
            let apiCall
            const params = {}

            // Add date range to params if provided
            if (dateRange.fromDate) {
                params.fromDate = dateRange.fromDate
            }
            if (dateRange.toDate) {
                params.toDate = dateRange.toDate
            }

            // Select the appropriate API function based on card type
            switch (cardType) {
                case 'sales':
                    apiCall = getSalesStatistics(params)
                    break
                case 'sst':
                    apiCall = getSSTStatistics(params)
                    break
                case 'manualTopup':
                    apiCall = getManualTopUpStatistics(params)
                    break
                case 'topup':
                    apiCall = getTopUpStatistics(params)
                    break
                case 'profit':
                    apiCall = getProfitStatistics(params)
                    break
                case 'user':
                    apiCall = getUserStatistics(params)
                    break
                case 'driver':
                    apiCall = getDriverStatistics(params)
                    break
                default:
                    throw new Error(`Unknown card type: ${cardType}`)
            }

            const response = await apiCall
            setCardData(prev => ({ ...prev, [cardType]: response }))
        } catch (error) {
            console.error(`Error fetching ${cardType} data:`, error)
            setCardData(prev => ({ ...prev, [cardType]: null }))
        } finally {
            setCardLoading(prev => ({ ...prev, [cardType]: false }))
        }
    }

    const barChartData = useMemo(() => {
        return [
            { name: t("COMPLETE"), value: data?.ride?.completed?.filter(e => dayjs(e.createdAt).isSame(dayjs(), "day")).length, color: "#007bff" },
            { name: t("PENDING"), value: data?.ride?.pending?.filter(e => dayjs(e.createdAt).isSame(dayjs(), "day")).length, color: "#F5A524" }
        ]
    }, [data])

    const pieChartData = useMemo(() => {
        return [
            { name: t("ACTIVE"), value: data?.driver?.active?.length, color: "#007bff" },
            { name: t("IDLE"), value: data?.driver?.idle?.length, color: "#006FEE" },
            { name: t("ON_TRIP"), value: data?.driver?.onTrip?.length, color: "#F5A524" },
            { name: t("OFFLINE"), value: data?.driver?.offline?.length, color: "#18181B80" },
        ]
    }, [data])

    const lineChartData = useMemo(() => {
        return data?.customer?.cancellations.map((e, i) => {
            return { hour: `${e.hour < 10 ? "0" : ""}${e.hour}:00`, value: e.orderCount + data?.customer?.completed[i].orderCount }
        })
    }, [data])

    return <PageWrapper headerProps={{ title: "DASHBOARD" }}>
        <Row className={"justify-content-center"} style={{ gap: "1.5rem" }}>
            <StatisticsChart
                data={cardData.sales}
                title={t("SALES")}
                prefix={"RM "}
                loading={cardLoading.sales}
                onTabChange={handleTabChange}
                cardType="sales"
            />
            <StatisticsChart
                data={cardData.sales}
                title={t("SST")}
                prefix={"RM "}
                loading={cardLoading.sst}
                onTabChange={handleTabChange}
                cardType="sst"
            />
            <StatisticsChart
                data={cardData.manualTopup}
                title={t("MANUAL_TOPUP")}
                prefix={"RM "}
                loading={cardLoading.manualTopup}
                onTabChange={handleTabChange}
                cardType="manualTopup"
            />
            <StatisticsChart
                data={cardData.topup}
                title={t("TOPUP")}
                prefix={"RM "}
                loading={cardLoading.topup}
                onTabChange={handleTabChange}
                cardType="topup"
            />
            <StatisticsChart
                data={cardData.profit}
                title={t("PROFIT")}
                prefix={"RM "}
                loading={cardLoading.profit}
                onTabChange={handleTabChange}
                cardType="profit"
            />
            <StatisticsChart
                decimalPlaces={0}
                data={cardData.user}
                title={t("NEW_USERS")}
                loading={cardLoading.user}
                onTabChange={handleTabChange}
                cardType="user"
            />
            <StatisticsChart
                decimalPlaces={0}
                data={cardData.driver}
                title={`${t("NEW")} ${t("DRIVER")}`}
                loading={cardLoading.driver}
                onTabChange={handleTabChange}
                cardType="driver"
            />

            {data && (
                <>
                    <Card className="shadow p-4" style={{ width: "640px" }}>
                        <h4>{t("TODAY_RIDES")}</h4>
                        <ResponsiveContainer width="100%" height={"100%"}>
                            <BarChart layout="vertical" data={barChartData} margin={{ left: 40, right: 20 }}>
                                <XAxis type="number" />
                                <YAxis dataKey="name" type="category" width={40} />
                                <Tooltip />
                                <Bar dataKey="value">
                                    {barChartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>

                    <Card className="shadow p-4" style={{ width: "640px" }}>
                        <h4>{t("DRIVER_STATUS")}</h4>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={pieChartData}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={80}
                                    dataKey="value"
                                    label
                                >
                                    {pieChartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </Card>

                    <Card className="shadow p-4" style={{ width: "640px" }}>
                        <h4>{t("DEMAND_BY_HOURS")}</h4>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={lineChartData}>
                                <XAxis dataKey="hour" />
                                <YAxis width={40} />
                                <Tooltip />
                                <CartesianGrid strokeDasharray="3 3" />
                                <Line type="monotone" dataKey="value" stroke="#007bff" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </Card>
                </>
            )}
        </Row>
    </PageWrapper>
};

export default Dashboard;
